import * as Sentry from '@sentry/remix';
import { withSentry } from '@sentry/remix';
import type { LinksFunction, LoaderFunctionArgs } from '@remix-run/node';
import { useEffect } from 'react';
import {
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData } from
'@remix-run/react';
import { useCaptureRouteError } from '~/utils/sentry-route-error';
import { usePageHandle } from './components/convenience/PageHandle';
import styles from './styles/app.css';
import type { PropsWithChildren } from 'react';
import { FullPageLayout, Fonts, ErrorPage, LinguiProvider } from './components';
import { SERVER_ENV } from './utils/env.server';
import { detectLocale } from './utils/i18n.server';

export const links: LinksFunction = () => {
  return [{ rel: 'stylesheet', href: styles }];
};

type DocumentProps = PropsWithChildren<{
  bodyClassName?: string;
  hydrate?: boolean;
  publicPath?: string;
  locale?: string;
}>;

export const loader = ({ request }: LoaderFunctionArgs) => {
  return {
    publicPath: SERVER_ENV.PUBLIC_PATH,
    buildTag: SERVER_ENV.BUILD_TAG,
    locale: detectLocale(request)
  };
};

const DocumentLayout = ({
  bodyClassName,
  hydrate,
  publicPath,
  locale = 'de',
  children
}: DocumentProps) => {
  return (
    <html lang={locale}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <title>Baumann & Partner S.A.</title>
        <Meta />
        <Links />
        <Fonts publicPath={publicPath ?? ''} />
      </head>
      <body className={bodyClassName}>
        {children}
        <ScrollRestoration />
        {hydrate && <Scripts />}
        {process.env.NODE_ENV === 'development' && <LiveReload />}
      </body>
    </html>);

};

function Root() {
  const loaderData = useLoaderData<typeof loader>();
  const { bodyClassName, hydrate } = usePageHandle();

  useEffect(() => {
    if (loaderData?.buildTag) {
      Sentry.setTag('release', loaderData.buildTag);
    }
  }, [loaderData?.buildTag]);

  return (
    <DocumentLayout
      publicPath={loaderData.publicPath}
      bodyClassName={bodyClassName}
      hydrate={hydrate}
      locale={loaderData.locale}>
      
      <LinguiProvider>
        <Outlet />
      </LinguiProvider>
    </DocumentLayout>);

}

export default withSentry(Root, {
  // Let Remix ErrorBoundary catch route errors so our root ErrorBoundary runs
  // and we render ErrorPage; we report to Sentry there via useCaptureRouteError.
  wrapWithErrorBoundary: false
});

export function ErrorBoundary() {
  useCaptureRouteError();
  return (
    <DocumentLayout>
      <FullPageLayout>
        <div className="h-screen flex flex-col justify-center">
          <ErrorPage />
        </div>
      </FullPageLayout>
    </DocumentLayout>);

}